/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import isEqual from 'lodash/isEqual'
import {useEffect, useState} from 'react'
import {
    useUsid,
    useShopperContext,
    useAccessToken,
    useShopperContextsMutation,
    useCommerceApi,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import {isServer, isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {useQueryClient} from '@tanstack/react-query'

// Hooks
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// Constants
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useShopperContextSearchParams} from '@salesforce/retail-react-app/app/hooks/use-shopper-context-search-params'
import {getProducts} from '@salesforce/commerce-sdk-react/hooks/ShopperProducts/queryKeyHelpers'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {DISABLED_GLOBALE_DEFAULTS, isGlobalEEnabled, useGlobaleContext} from '../contexts/globale'
import useCountryDetails from './use-country-details'
import {useSearchParams} from './use-search-params'
import {ARBITRARY_COUPON_CODE} from '../constants'
import {useLocation} from 'react-router-dom'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    getStoredSessionValue,
    publishKsExperimentForSessionKey,
    resolveMonetateCustomQualifiers,
    storeSessionValue
} from '../utils/monetate-ab-test-utils'

/*
 * This hook will set the shopper context when search params pertinant
 * to shopper context are present.
 */
export const useUpdateShopperContext = () => {
    const globalEEnabled = isGlobalEEnabled()
    const updateBasket = useShopperBasketsMutation('updateBasket')
    const {site} = useMultiSite()
    const {getUsidWhenReady} = useUsid()
    const [usid, setUsid] = useState('')
    const location = useLocation()

    const getUsid = async () => {
        const value = await getUsidWhenReady()
        setUsid(value || 'US') // Fixing the edge case
    }

    useEffect(() => {
        getUsid()
    }, [])

    const {getTokenWhenReady} = useAccessToken()
    const api = useCommerceApi()
    const {
        setCountryISOContext,
        setCountryForGlobale,
        setCurrencyForGlobale,
        setCurrencyContext,
        globaleCountry
    } = useGlobaleContext()
    const applyDisabledGlobalEDefaults = () => {
        setCountryISOContext(DISABLED_GLOBALE_DEFAULTS.countryISO)
        setCountryForGlobale(DISABLED_GLOBALE_DEFAULTS.globaleCountry)
        setCurrencyContext(DISABLED_GLOBALE_DEFAULTS.currencyCode)
        setCurrencyForGlobale(DISABLED_GLOBALE_DEFAULTS.globaleCurrency)
    }
    const {data: countryData} = useCountryDetails(globaleCountry, {enabled: globalEEnabled})
    const [searchParams] = useSearchParams()
    const queryClient = useQueryClient()
    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const {data: customer} = useCurrentCustomer()
    const createShopperContext = useShopperContextsMutation('createShopperContext')
    const updateShopperContext = useShopperContextsMutation('updateShopperContext')
    const {data: shopperContext, isLoading} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {enabled: !isServer && Boolean(usid)}
    )

    // Keep LeanConvert in sync with SFCC Shopper Context customQualifiers.
    useEffect(() => {
        const qualifiers = shopperContext?.customQualifiers
        if (!qualifiers) {
            return
        }
        Object.entries(qualifiers).forEach(([sessionKey, value]) => {
            publishKsExperimentForSessionKey(sessionKey, value)
        })
    }, [shopperContext?.customQualifiers])

    useEffect(() => {
        if (countryData?.currencyCode) {
            setCurrencyForGlobale(countryData?.currencyCode) // Using this to display price, wishlist.
        }

        if (countryData?.clientSettings) {
            try {
                const typeOfClientSettings = typeof countryData?.clientSettings
                const clientSettings =
                    typeOfClientSettings !== 'undefined' && typeOfClientSettings === 'object'
                        ? JSON.stringify(countryData?.clientSettings)
                        : countryData?.clientSettings
                localStorage.setItem('clientSettings', clientSettings)
            } catch (e) {
                console.warn(e)
            }
        }
    }, [countryData?.currencyCode, globaleCountry, countryData?.clientSettings])

    useEffect(() => {
        if (searchParams['affiliate']) {
            handleShopperContextUpdate(null, null, {sourceCode: searchParams['affiliate']})
        }
    }, [])

    useEffect(() => {
        const params = Object.fromEntries(new URL(window.location.href).searchParams)

        // This will execute only if user is opening the storefront from the Page Designer
        if (params.currentDateTime || params.effectiveDateTime) {
            handleShopperContextUpdate(null, null, {
                effectiveDateTime: params.currentDateTime || params.effectiveDateTime,
                customerGroupIds: params.customerGroups?.slice(1, -1)?.split(/,\s*/),
                sourceCode: params.currentSourceCode === 'undefined' ? '' : params.currentSourceCode
            })
        }
    }, [])

    // Handle updating the shopper context based on basket coupons
    const shopperContextFromSearchParams = useShopperContextSearchParams()
    const shopperContextFromBasket = basket?.couponItems?.length
        ? {
              couponCodes: basket?.couponItems?.length
                  ? basket?.couponItems?.map((c) => c.code)
                  : null
          }
        : {
              couponCodes: [ARBITRARY_COUPON_CODE]
          }

    const refetchDataOnClient = (isPageDesigner, pdUsid) => {
        if (isPageDesigner) {
            localStorage.setItem('pdUsid', pdUsid)
            queryClient.invalidateQueries()
        }

        const isCartOrCheckout = location.pathname === '/cart' || location.pathname === '/checkout'

        if (!basket?.couponItems?.length && !isCartOrCheckout) {
            return
        }

        queryClient.invalidateQueries({queryKey: ['use-plp-products-initial']})
        queryClient.invalidateQueries({queryKey: ['use-plp-products-remaining']})
        queryClient.invalidateQueries({queryKey: ['use-plp-search']})
        queryClient.invalidateQueries({queryKey: ['use-seo-category']})
        queryClient.invalidateQueries({queryKey: ['use-br-category']})
        queryClient.invalidateQueries({queryKey: ['use-scapi-products']})
        queryClient.invalidateQueries({queryKey: ['use-pdp-product']})
        queryClient.invalidateQueries({queryKey: ['use-product']})
        queryClient.invalidateQueries({
            queryKey: getProducts.path({
                organizationId: api.shopperExperience.clientConfig.parameters.organizationId
            })
        })
    }

    const mutateShopperContext = async (payload) => {
        const globaleDataRaw = cookie.get('GlobalE_Data')
        const globaleData = globaleDataRaw ? JSON.parse(globaleDataRaw) : {}
        const token = await getTokenWhenReady()
        const usid = atob(token?.split('.')?.[1])?.match(/usid:([^:"]+)/)?.[1]

        const url = `${api.shopperExperience.clientConfig.proxy}/shopper/shopper-context/v1/organizations/${api.shopperExperience.clientConfig.parameters.organizationId}/shopper-context/${usid}?siteId=${site.id}&evaluateContextWithClientIp=true`
        const existingContext = shopperContext ? shopperContext : {}
        const monetateQualifiers = await resolveMonetateCustomQualifiers({
            customerId: customer?.isRegistered && customer?.customerId ? customer.customerId : null
        })

        /**
         * NOTE:
         * We set customQualifiers (e.g. geCountry, Monetate A/B keys) so they
         * persist on session.custom when navigating to SFRA via session bridge.
         */
        let body = {
            ...existingContext,
            ...payload.body,
            clientIp: '',
            customQualifiers: {
                ...(existingContext.customQualifiers || {}),
                ...(payload.body?.customQualifiers || {}),
                geCountry: globalEEnabled ? globaleData.countryISO || 'US' : 'US',
                ...monetateQualifiers
            }
        }

        const response = await fetch(url, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            body: JSON.stringify(body)
        })

        const xGeo = response.headers.get('x-geolocation')
        const countryCode = xGeo?.split(';')?.[0]?.split(':')?.[1]?.trim()
        if (globalEEnabled) {
            setCountryForGlobale(globaleData.countryISO || countryCode) // Using this to display price, wishlist.
            setCountryISOContext(globaleData.countryISO || countryCode)
            if (globaleData?.currencyCode) {
                setCurrencyForGlobale(globaleData?.currencyCode)
            }
        } else {
            applyDisabledGlobalEDefaults()
        }
        cookie.set('countryCode', countryCode)
        cookie.set('city', xGeo?.match(/City:\s([^;]+)/)?.[1])
        cookie.set('state', xGeo?.match(/RegionCode:\s([^;]+)/)?.[1])

        if (response.ok && Object.keys(monetateQualifiers).length) {
            Object.entries(monetateQualifiers).forEach(([sessionKey, value]) => {
                if (!getStoredSessionValue(sessionKey)) {
                    storeSessionValue(sessionKey, value)
                } else {
                    publishKsExperimentForSessionKey(sessionKey, value)
                }
            })
            queryClient.invalidateQueries({queryKey: ['use-customer-groups']})
        }

        if (existingContext?.effectiveDateTime || existingContext?.customerGroupIds?.length) {
            // invalidate the queries if effectiveDateTime or customerGroupIds is set. i.e. user is either in page designer or in MRT
            queryClient.invalidateQueries()
        }
    }

    const handleShopperContextUpdate = async (shopperContext, newShopperContext, body) => {
        const token = await getTokenWhenReady() // We cannot get usid in the Page Designer
        const payload = {
            parameters: {
                usid: body ? atob(token?.split('.')?.[1])?.match(/usid:([^:"]+)/)?.[1] : usid,
                siteId: site.id
            },
            body: body || newShopperContext || shopperContextFromSearchParams
        }

        if (body) {
            // This logic will run only if page is opened from the Page Designer
            if (!shopperContext) {
                await createShopperContext.mutateAsync(payload)
            } else {
                await updateShopperContext.mutateAsync(payload)
            }
            await mutateShopperContext(payload)
            refetchDataOnClient(true, atob(token?.split('.')?.[1])?.match(/usid:([^:"]+)/)?.[1])
            return
        }

        await mutateShopperContext(payload)
        if (basket?.basketId) {
            await updateBasket.mutate({
                parameters: {basketId: basket?.basketId},
                body: {
                    c_refreshTimeStamp: new Date()
                }
            })
        }
        refetchDataOnClient()
    }

    useEffect(() => {
        if (isBasketLoading || window.bdayPromosUpdating) {
            return
        }

        const shouldUpdateShopperContext =
            !isLoading &&
            shopperContextFromBasket &&
            Object.keys(shopperContextFromBasket).length > 0 &&
            !isEqual(shopperContext, shopperContextFromBasket)

        if (shouldUpdateShopperContext && shopperContextFromBasket) {
            handleShopperContextUpdate(shopperContext, shopperContextFromBasket)
        } else if (shopperContext && basket?.couponItems?.length && isHydrated()) {
            refetchDataOnClient()
        } else if (
            shopperContextFromBasket === null &&
            window.location?.href.indexOf('currentDateTime') === -1 // don't execute the following line if user is in the Page Designer
        ) {
            handleShopperContextUpdate()
        }
    }, [basket?.couponItems?.length, shopperContext, isBasketLoading, isLoading])
}
